import { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGate, useUnit } from 'effector-react';
import {
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  Button,
} from '@mui/material';
import { Listbox } from '@features/effector-form/components/listbox';
import { BuildingControlProps } from '@features/effector-form/controls/building-controls/types';
import { Complex } from '@features/effector-form/controls/complexes/types';
import { withControl } from '@features/effector-form/hoc/withControl';
import { BuildingControlGate, buildingQuery } from '../model';
import * as Styled from './styled';

type Building = {
  id: number;
  complexId: number;
  label: string;
};

export const BuildingsControl = withControl<BuildingControlProps>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    complex,
    complexes = [],
    addAllButton,
    isError,
    placeholder,
  }) => {
    useGate(BuildingControlGate, { complex });
    const { t } = useTranslation();

    const { data, pending } = useUnit(buildingQuery);

    const updateValue = useCallback(
      (buildings: Building[]) => {
        const values = buildings
          .filter((building) => value.includes(building.id))
          .filter((building) => complexes?.includes(building.complexId));
        setValue(values.map(({ id }) => id));
      },
      [complexes, value]
    );

    const options = useMemo(() => {
      if (!data) return [];

      if (!complexes?.length && !complex) return data;

      const joinComplexes = complexes ?? [];
      if (complex) {
        joinComplexes.push(complex);
      }
      const filterByComplexId = ({ complexId }: { complexId: number }) =>
        joinComplexes.includes(complexId) ||
        joinComplexes.find((joinComplex) => joinComplex?.id === complexId);
      return data.filter(filterByComplexId);
    }, [data, complexes]);

    const getValue = () => options.filter(({ id }) => value.includes(id));

    const values = getValue();

    const addAllBuildings = () => {
      setValue(options.map(({ id }) => id));
    };

    useEffect(() => {
      /* ОБработка поля со зданиями, если удалил только один связывающий объект */
      if (
        (complexes?.length > 0 || complex) &&
        Array.isArray(data) &&
        data.length > 0 &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        updateValue(data);
      }
    }, [complexes]);

    const showError = !readOnly && !!error;

    return (
      <Styled.FormControl
        required={!disabled && !readOnly ? required : false}
        disabled={disabled}
      >
        {label && <FormLabel error={showError}>{label}</FormLabel>}
        <Styled.Autocomplete
          multiple
          loading={pending}
          value={values}
          options={options}
          onChange={(event: any, newValue: any) => {
            setValue(newValue.map((item: Complex) => item.id));
          }}
          size="small"
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              error={showError}
              InputProps={{
                ...params.InputProps,
                error: isError,
                readOnly,
                placeholder: !readOnly && values.length === 0 ? placeholder : undefined,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
          loadingText={t('Loading')}
          noOptionsText={t('DataNotFound')}
        />
        {addAllButton && !readOnly && (
          <Styled.AllBuildings>
            <Button onClick={addAllBuildings} variant="outlined" size="small">
              {t('AllBuildings')}
            </Button>
          </Styled.AllBuildings>
        )}
        {showError && <FormHelperText error>{error}</FormHelperText>}
      </Styled.FormControl>
    );
  }
);
